import { CloudErrors, SharedComputerUtils } from '@portal/portal-core';
import { castArray } from 'lodash-es';

const {
  DUPLICATE_SHARED_COMPUTER_IDENTIFIER,
  DUPLICATE_SHARED_COMPUTER_LABEL,
  MAX_SHARED_COMPUTERS,
  MAX_SHARED_COMPUTER_CHANGES,
  NO_MATCHING_SHARED_COMUPUTER,
  NO_REMAINING_SEATS,
} = CloudErrors.sharedComputersErrorMessages;

export const FINGERPRINT_DOCS_URL = 'https://flowjo.com/docs/flowjo11/license-info/fingerprint';
export const HWA_DOCS_URL = 'https://flowjo.com/docs/flowjo10/faq/general-faq/locating-hwa';

interface SharedComputer {
  id?: string;
  label?: string;
  [key: string]: unknown;
}

interface SharedComputerOptions {
  id?: string;
  label?: string;
  hwa?: string;
  contactLabel?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  fingerprint?: string;
}

type ValidationError = Record<string, string>;

export const getValidationErrors = (
  { id, label = '', hwa, contactLabel = '', startDate, endDate, fingerprint }: SharedComputerOptions,
  sharedComputerState: SharedComputer[] = [],
): ValidationError[] => {
  const filteredSharedComputerState = id
    ? sharedComputerState.filter((comp) => comp.id !== id)
    : sharedComputerState;
  const errorMessageArray: ValidationError[] = [];
  // Label
  if (!label) {
    errorMessageArray.push({ label: 'Label is required' });
  }
  if (label.length > 256) {
    errorMessageArray.push({ label: 'Label must be less than 256 characters' });
  }
  if (filteredSharedComputerState.find((comp) => comp.label === label)) {
    errorMessageArray.push({ label: 'There is already a shared computer with that label' });
  }
  // Contact Label
  if (contactLabel.length > 256) {
    errorMessageArray.push({ contactLabel: 'Contact label must be less than 256 characters' });
  }
  // Start Date
  if (!startDate) {
    errorMessageArray.push({ startDate: 'Start date is required' });
  }
  if (startDate && endDate && startDate > endDate) {
    errorMessageArray.push({ startDate: 'Start date must be before end date' });
  }
  if (!fingerprint && !hwa) {
    errorMessageArray.push({ fingerprint: 'Must include at least one identifier' });
  }
  // HWA
  if (hwa && hwa.length < 12) {
    errorMessageArray.push({ hwa: 'Hardware Address must be 12 hexidecimal characters' });
  }
  if (
    hwa &&
    filteredSharedComputerState.find(
      (comp) => SharedComputerUtils.getSharedComputerHwaIdentifierValue(comp) === hwa,
    )
  ) {
    errorMessageArray.push({
      hwa: 'There is already a license with that hardware address',
    });
  }
  // Fingerprint
  if (
    fingerprint &&
    filteredSharedComputerState.find(
      (comp) =>
        SharedComputerUtils.getSharedComputerFingerprintIdentifierValue(comp) === fingerprint,
    )
  ) {
    errorMessageArray.push({
      fingerprint: 'There is already a license with that fingerprint',
    });
  }
  if (fingerprint && fingerprint.length !== 16) {
    errorMessageArray.push({ fingerprint: 'Fingerprint must be 16 hexidecimal characters' });
  }
  return errorMessageArray;
};

export const getApiErrorLabels = (apiErrors?: string | string[]): string[] => {
  const errorMessageArray: string[] = [];
  castArray(apiErrors).forEach((apiError) => {
    /* istanbul ignore next */
    switch (apiError) {
      case DUPLICATE_SHARED_COMPUTER_IDENTIFIER:
        errorMessageArray.push('There is already a shared computer with that hardware identifier.');
        break;
      case DUPLICATE_SHARED_COMPUTER_LABEL:
        errorMessageArray.push('There is already a shared computer with that label.');
        break;
      case MAX_SHARED_COMPUTERS:
        errorMessageArray.push('You have reached your max number of shared computers.');
        break;
      case MAX_SHARED_COMPUTER_CHANGES:
        errorMessageArray.push('You have reached your max number of shared computer changes.');
        break;
      case NO_MATCHING_SHARED_COMUPUTER:
        errorMessageArray.push('There is no matching shared computer');
        break;
      case NO_REMAINING_SEATS:
        errorMessageArray.push('You have reached your max number of seats.');
        break;
      default:
        errorMessageArray.push('An error occured.');
    }
  });
  return errorMessageArray;
};
